import React, { useCallback, useEffect, useState } from 'react';
import HeaderWrapper from 'components/header';
import Sidebar from 'components/sidebar';
import { Box, Container, Grid, useTheme, useMediaQuery } from '@mui/material';
import { toast } from 'react-toastify';
import ProjectDialog from 'components/Project/AddEditProjectDialog';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDate } from 'components/dashboard/NewDashboard/Common/statusUtils';
// --- Existing API services (already in your codebase) ---
import StudentTutorialAPIs from 'utilities/api/StudentTutorialAPIs';
import StudentWorkshopList from 'utilities/api/workshop/StudentWorkshopList';
import WorkbenchBookingAPIs from 'utilities/api/StudentWorkbenchBookingAPIs';
import IssueInventoryAPIs from 'utilities/api/StudentIssueInventory';
import ProjectAPIs from 'utilities/api/StudentProjectAPI';
import MachineListAPIs from "utilities/api/StudentMachineList";
import BookMachineAPIs from 'utilities/api/StudentBookMachine';

// --- New dashboard components ---
import Greeting from 'components/dashboard/NewDashboard/Greeting';
import AttentionBanner from 'components/dashboard/NewDashboard/Attention/AttentionBanner';
import ProjectsSection from 'components/dashboard/NewDashboard/Projects/ProjectsSection';
import ActionItemsCard from 'components/dashboard/NewDashboard/ActionItems/ActionItemscard';
import TutorialsCard from 'components/dashboard/NewDashboard/Tutorials/TutorialsCard';
import WorkshopsCard from 'components/dashboard/NewDashboard/Workshops/WorkshopsCard';
import AvailableMachines from 'components/dashboard/NewDashboard/Machines/AvailableMachines';
import StatsSection from 'components/dashboard/NewDashboard/Stats/StatsSection';
import {
    mapWorkshopToCard,
    mapQuizToTutorialCard,
    buildAttentionItems,
    mapMachineToCard,
    isOverdue
} from 'components/dashboard/NewDashboard/dashboardTransforms';
import { computeDashboardStats } from 'components/dashboard/NewDashboard/statsTransforms';

const NewDashboard = () => {
    const { user, access } = useSelector((state) => state.user);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [projectDialogOpen, setProjectDialogOpen] = useState(false);

    const [rawProjects, setRawProjects] = useState([]);
    const [tutorialItems, setTutorialItems] = useState([]);
    const [workshopItems, setWorkshopItems] = useState([]);
    const [machineList, setMachineList] = useState([]);
    const [attentionItems, setAttentionItems] = useState([]);
    const [actionItems, setActionItems] = useState([]);
    const [dashboardStats, setDashboardStats] = useState(
        computeDashboardStats({}) // all zeros until first fetch resolves
    );

    // Wrapped in useCallback so the effect below can safely declare it as a
    // dependency instead of us having to remember to keep the dep array in sync.
    const fetchDashboardData = useCallback(async () => {
        const results = await Promise.allSettled([
            StudentTutorialAPIs.ModuleListGet(),
            StudentWorkshopList.WorkshopListGet(),
            WorkbenchBookingAPIs.getList(),
            BookMachineAPIs.ReservedMachineGet(),
            IssueInventoryAPIs.IssuedInventoryGet(),
            ProjectAPIs.ProjectsGet(),
            MachineListAPIs.FilterMachineListGet({
                accessToken: access,
                page: 1,
                page_size: 4,
            }),
        ]);

        const [
            tutorialsResult,
            workshopsResult,
            workbenchResult,
            reservationsResult,
            inventoryResult,
            projectsResult,
            machinesResult,
        ] = results;

        // --- Projects ---
        if (projectsResult.status === 'fulfilled' && projectsResult.value) {
            const projects =
                projectsResult.value?.Data ||
                projectsResult.value?.data ||
                (Array.isArray(projectsResult.value) ? projectsResult.value : []);
            setRawProjects(projects);
        } else {
            setRawProjects([]);
        }

        // --- Tutorials/Quizzes ---
        // NOTE: mapQuizToTutorialCard expects a matching QuizAttempt per quiz.
        // If ModuleListGet() doesn't already embed attempt/progress data,
        // add a follow-up call per quiz to /quizzes/:id/attempts-remaining/
        // before this is fully accurate. Passing null falls back to "Not Started".
        if (tutorialsResult.status === 'fulfilled' && tutorialsResult.value) {
            const modules =
                tutorialsResult.value?.Data ||
                tutorialsResult.value?.data ||
                (Array.isArray(tutorialsResult.value) ? tutorialsResult.value : []);

            setTutorialItems(
                modules.map((quiz) => mapQuizToTutorialCard(quiz, quiz.attempt || null)).slice(0, 2)
            );
        } else {
            setTutorialItems([]);
        }

        // --- Workshops ---
        if (workshopsResult.status === 'fulfilled' && workshopsResult.value) {
            const workshops =
                workshopsResult.value?.Data ||
                workshopsResult.value?.data ||
                (Array.isArray(workshopsResult.value) ? workshopsResult.value : []);
            setWorkshopItems(workshops.map(mapWorkshopToCard).slice(0, 2));
        } else {
            setWorkshopItems([]);
        }

        // --- Machines ---
        if (machinesResult.status === 'fulfilled' && machinesResult.value) {
            const machines =
                machinesResult.value?.results ||
                machinesResult.value?.Data ||
                machinesResult.value?.data ||
                (Array.isArray(machinesResult.value) ? machinesResult.value : []);

            setMachineList(machines.map(mapMachineToCard).slice(0, 4));
        } else {
            setMachineList([]);
        }

        const resolvedWorkbenchBookings =
            workbenchResult.status === 'fulfilled'
                ? workbenchResult.value?.Data ||
                  workbenchResult.value?.data ||
                  (Array.isArray(workbenchResult.value) ? workbenchResult.value : [])
                : [];

        const resolvedInventoryIssues =
            inventoryResult.status === 'fulfilled'
                ? inventoryResult.value?.Data ||
                  inventoryResult.value?.data ||
                  (Array.isArray(inventoryResult.value) ? inventoryResult.value : [])
                : [];

        const resolvedProjects =
            projectsResult.status === 'fulfilled'
                ? projectsResult.value?.Data ||
                  projectsResult.value?.data ||
                  (Array.isArray(projectsResult.value) ? projectsResult.value : [])
                : [];

        const resolvedReservations =
            reservationsResult.status === 'fulfilled'
                ? reservationsResult.value?.Data ||
                  reservationsResult.value?.data ||
                  (Array.isArray(reservationsResult.value) ? reservationsResult.value : [])
                : [];

        const attention = buildAttentionItems({
            reservations: resolvedReservations,
            workbenchBookings: resolvedWorkbenchBookings,
            inventoryIssues: resolvedInventoryIssues,
        });

        setAttentionItems(attention);

        // TODO: Figma's "Action Items" column (Pending/Rejected/Overdue tabs,
        // e.g. "Laser Cutter A3 — Level 2 · Requested Jun 12, 2026") is built
        // here as a filtered view over reservations + workbench bookings +
        // inventory issues (by approved_status). Revisit if a dedicated
        // mapActionItem-style transform/endpoint becomes available.
        const computedActionItems = [
            ...resolvedReservations
                .filter((r) => ['0', '2'].includes(r.approved_status))
                .map((r) => ({
                    id: `reservation-${r.id}`,
                    title: r.name,
                    subtitle: `${formatDate(r.reserved_date)}`,
                    status: r.approved_status === '0' ? 'Pending' : 'Rejected',
                    actionLabel: 'View Booking',
                })),

            ...resolvedWorkbenchBookings
                .filter((b) => ['0', '2'].includes(b.approved_status))
                .map((b) => ({
                    id: `workbench-${b.id}`,
                    title: `Workbench: ${b.project_name}`,
                    subtitle: `${formatDate(b.booking_date)} • ${b.start_time} - ${b.end_time}`,
                    status: b.approved_status === '0' ? 'Pending' : 'Rejected',
                    actionLabel: 'View Booking',
                })),

            ...resolvedInventoryIssues
                .filter((i) => i.returned_ === 'No' && isOverdue(i.issued_till))
                .map((i) => ({
                    id: `inventory-${i.id}`,
                    title: i.name,
                    subtitle: `Due ${formatDate(i.issued_till)}`,
                    status: 'Overdue',
                    actionLabel: 'Return Item',
                })),
        ];

        setActionItems(computedActionItems);

        // NOTE: `reservations: []` scenario means Active Bookings / Pending
        // Approvals will undercount until the machine_reservation_list
        // endpoint is confirmed wired in correctly.
        setDashboardStats(
            computeDashboardStats({
                reservations: resolvedReservations,
                workbenchBookings: resolvedWorkbenchBookings,
                inventoryIssues: resolvedInventoryIssues,
                projects: resolvedProjects,
            })
        );
    }, [access]);

    useEffect(() => {
        if (user?.TL_name) {
            fetchDashboardData();
        }
    }, [user?.TL_name, access, fetchDashboardData]);

    const handleSubmit = async (values) => {
        try {
            await ProjectAPIs.ProjectCreate(access, values);
            toast.success('Project created successfully!');
            setProjectDialogOpen(false);
            fetchDashboardData();
        } catch (error) {
            if (error.response?.status !== 400) {
                toast.error('Error submitting project. Please try again.');
            }
            console.error('Error submitting project:', error);
            throw error;
        }
    };

    const handleDismissAttention = (id) => {
        setAttentionItems((prev) => prev.filter((item) => item.id !== id));
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <Sidebar />

            <Box
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    backgroundColor: '#F1F4F9',
                    minHeight: '100vh',
                }}
            >
                <HeaderWrapper />

                <Container
                    maxWidth={false}
                    sx={{
                        mt: { xs: 2, md: 4 },
                        mb: { xs: 4, md: 8 },
                        pb: 4,
                        px: { xs: 2, sm: 4, md: 6 },
                    }}
                >
                    <AttentionBanner
                        items={attentionItems}
                        onDismiss={handleDismissAttention}
                        onAction={() => navigate('/explore')}
                    />
                    <Greeting name={user?.name || user?.username} />

                    <Box sx={{ mb: 4 }}>
                        <StatsSection stats={dashboardStats} />
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <ActionItemsCard
                                items={actionItems}
                                onAction={(item) => navigate(`/explore`)}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TutorialsCard
                                tutorials={tutorialItems}
                                onViewAll={() => navigate('/training')}
                                onAction={(id) => navigate(`/training`)}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <WorkshopsCard
                                workshops={workshopItems}
                                onViewAll={() => navigate('/training')}
                                onAction={(id) => navigate(`/training`)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <ProjectsSection
                                rawProjects={rawProjects}
                                onViewAll={() => navigate('/projects')}
                                onOpenProject={(id) => navigate(`/projects`)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <AvailableMachines
                                machines={machineList}
                                onViewAll={() => navigate('/explore')}
                            />
                        </Grid>
                    </Grid>
                </Container>

                <ProjectDialog
                    open={projectDialogOpen}
                    onClose={() => setProjectDialogOpen(false)}
                    handleSubmit={handleSubmit}
                    currentProject={null}
                    isMobile={isMobile}
                />
            </Box>
        </Box>
    );
};

export default NewDashboard;